<template>
  <div class="shop-section-nav__wrapper">
    <div :class="['shop-section-nav', subscriptionViewTeams && 'shop-section-nav--short']">
      <transition
        v-for="pill in navPills"
        :key="pill.id"
        name="shop-section-nav__fade"
        mode="out-in"
      >
        <Pill
          v-if="pill.show"
          nav
          :class="['shop-section-nav__pill', !pill.show && 'shop-section-nav__pill--hide']"
          :nav-selected="selectedPill === pill.id"
          @pill-click="handleClick(pill)"
        >
          {{ pill.text }}
        </Pill>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Pill from '@/components/shared/Pill.vue'
import debounce from 'lodash/debounce'

export default {
  name: 'ShopSectionNav',
  components: {
    Pill,
  },
  props: {
    isStickyBar: {
      type: Boolean,
      default: false,
    },
    defaultPill: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectedPill: '',
      clickedPill: '',
      currentScroll: 0,
    }
  },
  computed: {
    ...mapGetters({
      usersCurrentSubscriptions: 'subscriptions/getUsersCurrentSubscriptions',
      subscriptionViewTeams: 'app/isB2BOfferView',
      isBusinessUser: 'business/isBusinessUser',
      isBusinessPlanExpired: 'business/isBusinessPlanExpired',
      isSummerSavingsEnabled: 'featureFlags/isSummerSavingsEnabled',
    }),
    userHasSubscription() {
      return this.usersCurrentSubscriptions.length > 0
    },
    bundlesToggler() {
      if (this.isBusinessUser) {
        return this.isBusinessPlanExpired
      }
      if (this.userHasSubscription) {
        return false
      }
      return !this.subscriptionViewTeams
    },
    navPills() {
      const navPills = [
        {
          id: 'subscriptions',
          text: 'Subscriptions',
          show: true,
          headerOffset: 90,
        },
        {
          id: 'certification-prep-bundles',
          selector: 'bundles',
          text: 'Certification + Prep Bundles',
          show: this.bundlesToggler,
          headerOffset: 90,
        },
        {
          id: 'certifications',
          text: 'Certifications',
          show: true,
          headerOffset: 30,
        },
        {
          id: 'skill-dive',
          text: 'Skill Dive',
          show: !this.subscriptionViewTeams,
          headerOffset: 90,
        },
        {
          id: 'live-virtual-training',
          text: 'Live Virtual Training',
          show: true,
          headerOffset: 30,
        },
      ]

      if (this.isSummerSavingsEnabled) {
        const temp = navPills[1]
        navPills[1] = navPills[2]
        navPills[2] = temp
      }

      return navPills
    },
  },
  mounted() {
    if (this.defaultPill) {
      this.selectedPill = this.defaultPill
      this.clickedPill = this.defaultPill
    }
    window.addEventListener('scroll', this.getScrollTop)
    // create this watcher only if the sticky bar is true
    if (this.isStickyBar) {
      this.$watch('subscriptionViewTeams', () => {
        // if created
        // execute this watcher only if the sticky bar is true
        if (this.isStickyBar) {
          const currentSections = this.navPills.map(
            el => !!document.querySelector(`[class*="__content-${el.selector || el.id}"]`)
          )
          const currentIndex = this.navPills.findIndex(pill => pill.id === this.clickedPill)

          setTimeout(() => {
            if (currentSections[currentIndex]) {
              this.scrollToElement(this.navPills[currentIndex].id)
            } else {
              // go to subscriptions section
              this.scrollToElement(this.navPills[0].id)
              this.clickedPill = ''
            }
          }, 1000)
        }
      })
    }
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.getScrollTop)
  },
  methods: {
    debouncedHistoryUpdate: debounce(id => {
      // params: state, title, url
      window.history.replaceState(null, null, `/#${id}`)
    }, 300),
    scrollToElement(value) {
      const navPill = this.navPills.find(pill => pill.id === value)
      const elementContent = document.querySelector(
        `[class*="__content-${navPill.selector || navPill.id}"]`
      )

      const elementPosition = elementContent.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - navPill.headerOffset
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    },
    getScrollTop() {
      this.navPills.forEach(pill => {
        const elementContent = document.querySelector(
          `[class*="__content-${pill.selector || pill.id}"]`
        )
        if (elementContent) {
          const elementPosition = elementContent.getBoundingClientRect().top
          if (elementPosition < 100) {
            this.selectedPill = pill.id
            this.debouncedHistoryUpdate(pill.id)
          }
        }
      })
    },
    handleClick(pill) {
      this.clickedPill = pill.id
      this.scrollToElement(pill.id)
    },
  },
}
</script>

<style scoped lang="scss">
.shop-section-nav {
  color: var(--Secondary-Light-Blue-Gray, #afb0bf);
  font-feature-settings: 'liga' off, 'clig' off;
  @include font-book;
  font-size: 12px;
  font-style: normal;
  font-weight: 450;
  line-height: 14px;

  display: flex;
  justify-content: center;
  width: 645px;
  transition: width 1s ease-in-out;
  @include tablet {
    justify-content: flex-end;
  }
  &--short {
    width: 360px;
    @include tablet {
      width: 350px;
    }
  }
  &__pill {
    margin-left: 10px;
  }
  &__wrapper {
    display: flex;
    justify-content: flex-start;
    width: 100%;
    overflow-x: scroll;
    &::-webkit-scrollbar {
      height: 2px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: var(--purple-pale);
      border-radius: 10px;
    }
    &::-webkit-scrollbar-track {
      background: var(--gray-7);
    }
    padding-bottom: 3px;
    @include mobile {
      overflow-x: hidden;
      padding-bottom: unset;
      justify-content: center;
    }
    @include desktop {
      justify-content: flex-end;
    }
  }
  &__fade {
    &-enter-active,
    &-leave-active {
      transition: all 1s ease-in-out;
      width: 100%;
    }
    &-enter,
    &-leave-to {
      transition: all 1s ease-in-out;
      opacity: 0;
      width: 0;
      padding: 10px 0;
      margin-left: 0;
    }
  }
}
</style>
